import moment from 'moment';

import {assert, assertAndContinue} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {isAnalyticsQueryViewStateId} from 'insights/analytics-queries/AnalyticsQueryViewState';
import runWarehouseQueryGlobalState from 'insights/analytics-queries/run-query/runWarehouseQueryGlobalState';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {
  getExportQueryData,
  getRunQueryData,
  restGetRunQueryData,
} from 'insights/parameters/data/AnalyticsQueryOperations';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import {exportUntrustedSQLQueryOutputToCSV} from 'insights/warehouse/AnalyticsWarehouseActions';
import {RunQueryData, runUntrustedSQLQuery} from 'insights/warehouse/AnalyticsWarehouseDAO';
import {ExportType, RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import {AsyncStatus, makeAsyncCall} from 'ui-library/hooks/useAsyncState';
import {formalTime} from 'util/DateUtil';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import trackRunQuery, {ExtraDataToTrackRunQuery} from './trackRunQuery';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

export function logErrorIfFromTemplateForm(
  data: RunQueryData,
  errorMessage: string,
  extraData: ExtraDataToTrackRunQuery
): void {
  if (extraData.sqlTemplateFormType != null) {
    assertAndContinue(false, 'Generated query that caused an error using sql templating', {
      errorMessage,
      query: data.parameterizedSQLQuery,
    });
  }
}

export function runWarehouseQuery(
  data: RunQueryData,
  currentState: RunQueryState,
  setAsyncState: (nextState: RunQueryState) => void
): void {
  if (currentState.status === AsyncStatus.LOADING) {
    assertAndContinue(
      false,
      'Unexpectedly ran query while one is still running. Check the trace to find where the user can rerun the query in parallel and disallow it.'
    );
    return;
  }

  makeAsyncCall({
    makeCall: runUntrustedSQLQuery,
    args: [data],
    asyncState: currentState,
    setAsyncState,
  });
}

export function runAnalyticsQuery(
  analyticsQuery: AnalyticsQuery,
  analyticsQueryViewStateId: string,
  data: RunQueryData,
  currentState: RunQueryState,
  extraData: ExtraDataToTrackRunQuery
): void {
  assert(
    isAnalyticsQueryViewStateId(analyticsQueryViewStateId),
    "Expected to use runAnalyticsQuery with an analyticsQueryViewState's id"
  );
  runWarehouseQuery(data, currentState, (nextState: RunQueryState) => {
    runWarehouseQueryGlobalState.updateState(analyticsQueryViewStateId, nextState);
    trackRunQuery(analyticsQuery, convertRunQueryStateToLegacyState(nextState), extraData);

    if (nextState.status === AsyncStatus.ERROR) {
      if (nextState.responseJSON.status === 400) {
        logErrorIfFromTemplateForm(data, nextState.message, extraData);
      }
    }
  });
}

export function runGraphQLAnalyticsQuery(
  analyticsQuery: AnalyticsQuery,
  analyticsQueryViewStateId: string,
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
  parameterValues: Array<AnalyticsParameterValue>,
  currentState: RunQueryState,
  extraData: ExtraDataToTrackRunQuery
): void {
  const runQueryData = getRunQueryData({
    parameterConfigs,
    parameterValues,
    sqlQuery: analyticsQuery.sqlQuery,
  });
  runAnalyticsQuery(analyticsQuery, analyticsQueryViewStateId, runQueryData, currentState, extraData);
}

export function runSubQuery(
  analyticsQuery: AnalyticsQuery,
  sqlSubQuery: string,
  currentState: RunQueryState,
  parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>,
  parameterValues: Array<RESTAnalyticsParameterValue>,
  setAsyncState: (nextState: RunQueryState) => void,
  extraData: ExtraDataToTrackRunQuery
): void {
  const runQueryData = restGetRunQueryData({
    parameterConfigs,
    parameterValues,
    sqlQuery: sqlSubQuery,
  });
  runWarehouseQuery(runQueryData, currentState, (nextState: RunQueryState) => {
    setAsyncState(nextState);
    trackRunQuery(analyticsQuery, convertRunQueryStateToLegacyState(nextState), extraData, sqlSubQuery);
  });
}

export function exportAnalyticsQueryOutputToCSV(
  gqlAnalyticsQuery: Pick<AnalyticsQuery, 'sqlQuery' | 'name'>,
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
  parameterValues: Array<AnalyticsParameterValue>,
  {dashboardName, exportType}: {dashboardName: string; exportType: ExportType}
): void {
  const filename = `${formalTime(moment())} ${dashboardName} - ${gqlAnalyticsQuery.name}`.replace(/\//g, '-');
  const exportQueryData = getExportQueryData({
    exportType,
    filename,
    parameterConfigs,
    parameterValues,
    sqlQuery: gqlAnalyticsQuery.sqlQuery,
  });
  exportUntrustedSQLQueryOutputToCSV(exportQueryData);
}
